// feature-dispatch-gate-file-reader.ts
//
// Đọc gates.json và phân loại status, tách khỏi feature-dispatch-gate-guard.ts
// (298 dòng, quá ngân sách 200). Guard giữ phần QUYẾT ĐỊNH cho dispatch; file
// này giữ phần "file nói gì, và cái gì tính là đã có thẩm quyền".

import { readFile } from "node:fs/promises";
import { join } from "node:path";
import { GATE_FILE_NAME, type GateId, type GateStatus } from "./gate-registry.js";

/** Gate statuses that count as "authority granted". `waived` is a legitimate terminal state per spec §8 (recorded delegated authority), `invalidated` is NOT. */
export const APPROVED_GATE_STATUSES: readonly GateStatus[] = ["approved", "waived"];

/** Every status the spec's enum allows — anything else means the file is not what we think it is. */
const KNOWN_GATE_STATUSES: readonly string[] = [
  "not_requested",
  "pending",
  "approved",
  "waived",
  "invalidated",
];

/**
 * Gates that must be granted before any feature ticket dispatches.
 *
 * G2 (P16) is conditional, unlike G0/G1: it only applies when the plan
 * actually tripped one of spec §8's conditions, which
 * g2-dispatch-gate-enforcement.ts records into gates.json as `pending` (needs
 * a human) or `waived` (small mission, G0 delegated). So `not_requested` for
 * g2 legitimately means "this plan does not need it" — whereas the same value
 * on g0/g1 would mean the mission never even asked, which is why those two
 * are unconditional.
 */
export const REQUIRED_GATES: readonly GateId[] = ["g0", "g1"];

/** Conditional gates: enforced only once something has actually requested them. */
export const CONDITIONAL_GATES: readonly GateId[] = ["g2"];

/**
 * Reads gates.json STRICTLY — unlike `readGateFile`, which swallows a
 * missing or corrupt file into all-`not_requested`. That coercion happens to
 * block here (not_requested is not an approval), but it would report
 * "gate chưa duyệt" for a file that is actually unreadable, sending the
 * operator to approve a gate when the real problem is a damaged control
 * file. Returning undefined keeps the two causes distinguishable.
 */
export async function readGateStatusesStrict(
  controlRoot: string,
): Promise<Record<GateId, GateStatus> | undefined> {
  let raw: string;
  try {
    raw = await readFile(join(controlRoot, GATE_FILE_NAME), "utf8");
  } catch {
    return undefined; // absent, EISDIR, permissions — authority state UNKNOWN.
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (typeof parsed !== "object" || parsed === null) return undefined;

  const record = parsed as Record<string, unknown>;
  const statuses: Partial<Record<GateId, GateStatus>> = {};
  for (const gate of [...REQUIRED_GATES, ...CONDITIONAL_GATES]) {
    const entry = record[gate];
    if (typeof entry !== "object" || entry === null) return undefined;
    const status = (entry as Record<string, unknown>).status;
    // An unrecognised status string is NOT treated as "some new kind of
    // pending" — the file is not the shape this code reasons about, so the
    // honest answer is "unknown", which fails closed.
    if (typeof status !== "string" || !KNOWN_GATE_STATUSES.includes(status)) return undefined;
    statuses[gate] = status as GateStatus;
  }
  return statuses as Record<GateId, GateStatus>;
}
